import { TableClient } from "@azure/data-tables";
import type { TokenCredential } from "@azure/identity";
import {
  TABLE,
  PK,
  RK,
  type InstalledStamp,
  stampToEntity,
  entityToStamp,
} from "@m8t-stack/platform-release";
import { discoverStampStorage } from "./platform-storage-discovery.js";
import { upsertMetadataEntity, makeSharedKeyClient, is403, is404 } from "./metadata-table-write.js";

export { stampToEntity, entityToStamp };
export type { InstalledStamp, ComponentState, ImageStampEntry, PersonaStampEntry } from "@m8t-stack/platform-release";

/**
 * Write the platform stamp. The auth ladder (AAD → shared key → self-grant +
 * backoff) lives in metadata-table-write.ts and is shared with the
 * system/infra-params writer — an installer MI holds Owner at subscription
 * scope but no Storage Table data role, so both rows need the same fallbacks.
 */
export async function writeStamp(opts: {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
  stamp: InstalledStamp;
  onProgress?: (m: string) => void;
}): Promise<void> {
  // stampToEntity's return type is deliberately the wire-shape `Record<string, unknown>`
  // (this is the contract downstream tasks import); it always carries partitionKey/rowKey
  // at runtime, so this narrows the type for the SDK's `TableEntity<T>` constraint.
  const entity = stampToEntity(opts.stamp) as Record<string, unknown> & { partitionKey: string; rowKey: string };
  await upsertMetadataEntity({
    credential: opts.credential,
    subscriptionId: opts.subscriptionId,
    resourceGroup: opts.resourceGroup,
    entity,
    label: "platform stamp",
    errorCode: "PLATFORM_STAMP_NO_ACCESS",
    retryHint: "The role assignment persists — retry 'm8t platform update' in a few minutes.",
    ...(opts.onProgress ? { onProgress: opts.onProgress } : {}),
  });
}

/**
 * The three distinct outcomes a stamp read can have — mirrors `readPolicy`'s
 * `PolicyReadResult` (platform-policy.ts). "The record genuinely does not
 * exist" and "the read failed and the live record is unknown" must never
 * collapse into the same value: a caller that needs to know the PRIOR record
 * (lineage, carried-forward fields) has to be able to refuse to act on an
 * unreadable read rather than treating it as "nothing was ever there".
 */
export type StampReadResult =
  | { source: "absent" }
  | { source: "explicit"; stamp: InstalledStamp }
  | { source: "unreadable" };

/** The slice of `TableClient` a stamp READ uses — the seam the unit tests fake. */
export interface StampReadClient {
  getEntity(partitionKey: string, rowKey: string): Promise<Record<string, unknown>>;
}

export interface ReadStampOpts {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
  // Test seams — production callers pass none of these. They mirror
  // `UpsertMetadataOpts`' seams so both halves of the ladder are drivable the
  // same way; the read half had none, which is why the 404 asymmetry below
  // shipped untested.
  discoverImpl?: typeof discoverStampStorage;
  aadClientImpl?: (tableEndpoint: string, credential: TokenCredential) => StampReadClient;
  sharedClientImpl?: (o: {
    tableEndpoint: string;
    accountResourceId: string;
    accountName: string;
  }) => Promise<StampReadClient | null>;
}

/**
 * Read the platform stamp, distinguishing "genuinely absent" from "unreadable".
 * The auth ladder (AAD → shared key) is the same one `readStamp` uses.
 */
export async function readStampOutcome(opts: ReadStampOpts): Promise<StampReadResult> {
  const discover = opts.discoverImpl ?? discoverStampStorage;
  const makeAad =
    opts.aadClientImpl ??
    ((endpoint: string, credential: TokenCredential): StampReadClient => new TableClient(endpoint, TABLE, credential));
  const makeShared = opts.sharedClientImpl ?? makeSharedKeyClient;

  const { tableEndpoint, accountResourceId, accountName } = await discover(opts);
  const aad = makeAad(tableEndpoint, opts.credential);
  try {
    const row = await aad.getEntity(PK, RK);
    const stamp = entityToStamp(row);
    return stamp ? { source: "explicit", stamp } : { source: "unreadable" };
  } catch (e) {
    if (is404(e)) return { source: "absent" };
    if (!is403(e)) return { source: "unreadable" };
  }

  try {
    const shared = await makeShared({ tableEndpoint, accountResourceId, accountName });
    if (!shared) return { source: "unreadable" };
    const row = await shared.getEntity(PK, RK);
    const stamp = entityToStamp(row);
    return stamp ? { source: "explicit", stamp } : { source: "unreadable" };
  } catch (e) {
    // A 404 is absence on EITHER rung, and this arm used to swallow that. The
    // installer MI reaches ONLY this rung (Owner carries no Storage Table
    // dataActions, so the AAD read above always 403s for it), and on a from-zero
    // install the row is genuinely absent here — `seed_platform_stamp` runs a
    // phase LATER than the executor deploy that reads it. So every such install
    // read "no row yet" as "unreadable", and `azure-exec deploy` skipped
    // provisioning outbound email rather than risk a duplicate ACS.
    if (is404(e)) return { source: "absent" };
    return { source: "unreadable" };
  }
}

/**
 * Read the platform stamp. Must stay robust for a founder who hasn't been
 * granted the data-plane role: a 403 on the AAD read falls back to
 * shared-key so `status`/`update` don't perpetually see "unmanaged" and
 * re-adopt. Any other failure (404, or the shared-key fallback erroring
 * too) is treated as "no stamp" — this is a best-effort read, for callers
 * that only need a display/adopt-vs-update signal rather than the previous
 * record's lineage. A caller that must not conflate "absent" with
 * "unreadable" should use `readStampOutcome` instead.
 */
export async function readStamp(opts: ReadStampOpts): Promise<InstalledStamp | null> {
  const result = await readStampOutcome(opts);
  return result.source === "explicit" ? result.stamp : null;
}
